import React from 'react';
import styled from 'styled-components';

import { MultiAnswerPanelBlock } from '../../types';
import HighlightedText from '../HighlightedText';
import { ResponseActions } from '../ResponseActionButton';
import { useResponseAction } from '../responseActions';
import { ResponseIntro, ResponsePanel, ResponseReply } from '../responsePrimitives';
import { responseType, rsColor, rsv } from '../responseScale';

const Panel = styled(ResponsePanel)`
  padding: ${rsv.space('answerPanel')};

  > * + * {
    margin-top: ${rsv.space('answerRowGap')}px;
  }
`;

// 6265:4679.
const Row = styled.article`
  padding: ${rsv.space('answerRow')};
  border: 1px solid ${rsColor.border};
  border-radius: ${rsv.radius('row')}px;
  background: ${rsv.color('answerRow')};
`;

// 6265:4681: the payload sends "Answer 1 of 3"; the design upper-cases it.
const Label = styled.div`
  color: ${rsv.color('label')};
  text-transform: uppercase;
  ${responseType('answerLabel')}
`;

const Title = styled.h4`
  margin: 0;
  color: ${rsColor.text};
  ${responseType('rowTitle')}

  ${Label} + & {
    margin-top: ${rsv.space('answerLabelToTitle')}px;
  }
`;

const Body = styled.p`
  margin: ${rsv.space('answerTitleToBody')}px 0 0;
  color: ${rsColor.secondary};
  white-space: pre-line;
  ${responseType('answerBody')}
`;

const Links = styled(ResponseActions)`
  margin-top: ${rsv.actionsTop('answerBodyToLink')}px;

  > [data-look='link'] {
    ${responseType('answerLink')}
  }
`;

/** Figma 6423:10977: a plain intro, then one panel of answer rows (6265:4677). */
const MultiAnswerPanel: React.FC<{ block: MultiAnswerPanelBlock }> = ({ block }) => {
  const emit = useResponseAction(block);
  return (
    <ResponseReply
      $column="replyColumnNarrow"
      data-testid="orbit-response-multi_answer_panel"
      data-block-id={block.id}
    >
      {block.intro ? (
        <ResponseIntro>
          <HighlightedText text={block.intro} highlights={block.intro_highlights} />
        </ResponseIntro>
      ) : null}
      <Panel>
        {block.items.map(item => (
          <Row key={item.id} data-testid="orbit-response-answer" data-item-id={item.id}>
            {item.label ? <Label>{item.label}</Label> : null}
            <Title>{item.title}</Title>
            <Body>{item.body}</Body>
            <Links actions={item.actions} onAction={emit} />
          </Row>
        ))}
      </Panel>
    </ResponseReply>
  );
};

export default MultiAnswerPanel;
